import * as React from 'react';

import { useDebounceFn, useRequest } from 'ahooks';
import { Col, Form, Input, Row, Select, Spin, Typography } from 'antd';

import {
  CheckInputbox,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  PrimitiveTreeSelect,
  RichTextEditor,
} from 'components';
import { useProductAction, useProductValue } from 'context';
import { getBrands, getCategories, searchTags } from 'services';
import type { SelectOption, TreeSelectSource } from 'types';
import {
  findElementByKey,
  findTreeElementByKey,
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  normalizeToString,
  PATHNAME,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

type TagsParams = Parameters<typeof searchTags>[number];

type FieldType = Pick<
  ReturnType<typeof useProductValue>,
  | 'name'
  | 'identifier'
  | 'slug'
  | 'brand'
  | 'mainCategory'
  | 'breadcrumbCategory'
  | 'isRecommended'
  | 'callForPrice'
  | 'showPostingStatus'
  | 'otherCategories'
  | 'tags'
  | 'description'
>;

const ProductBaseInfo = () => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });
  const {
    data: tagsData,
    error: tagsError,
    params: tagsParams,
    loading: isTagsLoading,
    run: searchTagsRun,
  } = useRequest(
    async ({ searchKey }: Pick<TagsParams, 'searchKey'>) =>
      await searchTags({
        searchKey,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );

  const {
    name = '',
    identifier = '',
    slug = '',
    isRecommended = false,
    callForPrice = false,
    showPostingStatus = false,
    description = '',
    ownerType,
  } = useProductValue();
  const action = useProductAction();

  const { run: debounceUpdateName } = useDebounceFn(
    (debouncedName: typeof name) => {
      action({
        type: 'UPDATE_PRODUCT_NAME',
        payload: {
          name: debouncedName,
        },
      });
    },
  );
  const { run: debounceUpdateIdentifier } = useDebounceFn(
    (debouncedIdentifier: typeof identifier) => {
      action({
        type: 'UPDATE_PRODUCT_IDENTIFIER',
        payload: {
          identifier: debouncedIdentifier,
        },
      });
    },
  );
  const { run: debounceUpdateSlug } = useDebounceFn(
    (debouncedSlug: typeof slug) => {
      action({
        type: 'UPDATE_PRODUCT_SLUG',
        payload: {
          slug: debouncedSlug,
        },
      });
    },
  );
  const { run: debounceUpdateDescription } = useDebounceFn(
    (debouncedDescription: typeof description) => {
      action({
        type: 'UPDATE_PRODUCT_DESCRIPTION',
        payload: {
          description: debouncedDescription,
        },
      });
    },
  );

  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};
  const { total: totalTags = 0, data: tags = [] } = tagsData ?? {};
  const { searchKey: currentTagSearchKey = '' } = tagsParams.at(0) ?? {};

  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );
  const categoriesSelectOptions = generateSelectOptions(
    categories,
    'id',
    'title',
    'id',
  );
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const tagsSelectOptions = generateSelectOptions(tags, 'id', 'title', 'id');

  if (!ownerType) return <React.Fragment />;

  return (
    <Row>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نام'
          name='name'
          rules={[inputRule.required, inputRule.string]}
        >
          <Input
            placeholder='نام محصول را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateName(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='شناسه'
          name='identifier'
          rules={[inputRule.nonDot]}
        >
          <Input
            placeholder='شناسه محصول را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateIdentifier(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='پیوند یکتا (Slug)'
          name='slug'
          rules={[inputRule.required, inputRule.nonDot]}
        >
          <Input
            placeholder='پیوند یکتا محصول را وارد کنید'
            onChange={({ target: { value } }) => {
              debounceUpdateSlug(value);
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='برند'
          name='brand'
          rules={[inputRule.required]}
        >
          <Select<SelectOption | undefined>
            allowClear
            labelInValue
            showSearch
            maxTagCount='responsive'
            placeholder='برند محصول را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={value => {
              const brandId = normalizeToString(value?.value);
              const selectedBrand = findElementByKey(brands, 'id', brandId);

              action({
                type: 'UPDATE_PRODUCT_BRAND',
                payload: {
                  brand: selectedBrand,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='دسته‌بندی اصلی'
          name='mainCategory'
          rules={[inputRule.required]}
        >
          <Select<SelectOption | undefined>
            allowClear
            labelInValue
            showSearch
            maxTagCount='responsive'
            placeholder='دسته‌بندی اصلی محصول را انتخاب کنید'
            loading={categoriesLoading}
            options={categoriesSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
            onChange={value => {
              const mainCategoryId = normalizeToString(value?.value);
              const selectedMainCategory = findElementByKey(
                categories,
                'id',
                mainCategoryId,
              );

              action({
                type: 'UPDATE_PRODUCT_MAIN_CATEGORY',
                payload: {
                  mainCategory: selectedMainCategory,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='دسته‌بندی ناوبری (Breadcrumb)'
          name='breadcrumbCategory'
        >
          <PrimitiveTreeSelect<TreeSelectSource | undefined>
            disabled
            labelInValue
            placeholder='دسته‌بندی ناوبری محصول را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
            onChange={value => {
              const breadcrumbCategoryId = normalizeToString(value?.value);
              const selectedBreadcrumbCategory = findTreeElementByKey(
                categories,
                'id',
                breadcrumbCategoryId,
              );

              action({
                type: 'UPDATE_PRODUCT_BREADCRUMB_CATEGORY',
                payload: {
                  breadcrumbCategory: selectedBreadcrumbCategory,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='پیشنهاد ویژه'
          name='isRecommended'
          valuePropName='checked'
          initialValue={isRecommended}
        >
          <CheckInputbox>نمایش نشان پیشنهاد ویژه</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='استعلام بها'
          name='callForPrice'
          valuePropName='checked'
          initialValue={callForPrice}
        >
          <CheckInputbox>نیاز به استعلام بها</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='وضعیت ارسال'
          name='showPostingStatus'
          valuePropName='checked'
          initialValue={showPostingStatus}
        >
          <CheckInputbox>نمایش وضعیت ارسال</CheckInputbox>
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType> label='دسته‌بندی‌های فرعی' name='otherCategories'>
          <PrimitiveTreeSelect<TreeSelectSource[]>
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های فرعی محصول را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
            onChange={value => {
              const selectedOtherCategories = value.map(({ value }) => {
                const otherCategoryId = normalizeToString(value);
                const selectedOtherCategory = findTreeElementByKey(
                  categories,
                  'id',
                  otherCategoryId,
                )!;

                return selectedOtherCategory;
              });

              action({
                type: 'UPDATE_PRODUCT_OTHER_CATEGORIES',
                payload: {
                  otherCategories: selectedOtherCategories,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType> label='برچسب‌ها' name='tags'>
          <Select<SelectOption[] | undefined>
            allowClear
            labelInValue
            filterOption={false}
            mode='multiple'
            placeholder='برچسب‌های محصول را انتخاب کنید'
            options={tagsSelectOptions}
            notFoundContent={
              isTagsLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری برچسب‌ها...
                  </Typography.Text>
                </div>
              ) : tagsError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {tagsError instanceof Error
                      ? tagsError.message
                      : String(tagsError)}
                  </Typography.Text>
                </div>
              ) : !totalTags ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ برچسبی با عنوان</span>
                    <span> &rdquo;{currentTagSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim();
              searchTagsRun({ searchKey });
            }}
            onChange={(value = []) => {
              const selectedTags = value.map(({ value, label }) => {
                const tagId = normalizeToString(value);
                const tagTitle = normalizeToString(label);
                const selectedTag = findTreeElementByKey(tags, 'id', tagId)!;

                return {
                  ...(selectedTag ?? {}),
                  id: tagId,
                  title: tagTitle,
                };
              });

              action({
                type: 'UPDATE_PRODUCT_TAGS',
                payload: {
                  tags: selectedTags,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='توضیحات' name='description'>
          <RichTextEditor
            ownerType={ownerType}
            options={{
              content: description,
              onUpdate: ({ editor }) => {
                debounceUpdateDescription(editor.getHTML());
              },
            }}
            config={{
              placeholder: {
                placeholder: 'توضیحات محصول را وارد کنید',
              },
            }}
            className='xl:max-w-full'
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { ProductBaseInfo };
